import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faWallet,
  faClock,
} from '@fortawesome/free-solid-svg-icons';

import { Button } from '@/components/ui/Button';
import { OtpInput } from '@/components/ui/OtpInput';
import {
  clearAuthError,
  generateOtpRequest,
  resetAuthState,
  verifyOtpRequest,
} from '@/store/actions/authActions';
import type { AppDispatch, RootState } from '@/store';
import type { OtpDeliveryMethod, OtpFormValues } from '@/types/auth.types';
import { getDeviceInfo } from '@/utils/deviceInfo';
import { isExactLength, isOnlyDigits, isRequired } from '@/utils/validation';
import { AUTH_ROUTES } from '@/constants/routes/authRoutes';
import { AUTH_LABELS } from '@/constants/static/loginLabels';
import { DeliveryMethodSelector } from '@/components/common/DeliveryMethodSelector';
import { ContactInfo } from '@/components/common/ContactInfo';

import skyline from '../../assets/images/skyline.svg';

import '../Register/Register.css';
import './Otp.css';

interface LocationState {
  mobileNumber?: string;
  email?: string;
  transactionId?: string;
  deliveryMethod?: OtpDeliveryMethod;
}

const initialFormValues: OtpFormValues = {
  otp: '',
};

interface FormErrors {
  otp?: string;
}

const maskMobileNumber = (mobileNumber: string): string => {
  const clean = mobileNumber.replace(/\D/g, '');
  if (clean.length <= 3) return `+91 ${clean}`;
  const lastThree = clean.slice(-3);
  const masked = 'X'.repeat(clean.length - 3);
  return `+91 ${masked}${lastThree}`;
};

const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');
  if (!domain) return email;
  if (localPart.length <= 1) return email;
  const firstChar = localPart.charAt(0);
  const maskedLocal = `${firstChar}${'*'.repeat(localPart.length - 1)}`;
  return `${maskedLocal}@${domain}`;
};

const RESEND_DELAY_SECONDS = 30;
const OTP_EXPIRY_SECONDS = 120;
const MAX_OTP_ATTEMPTS = 5;

const formatCountdown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const remainingSeconds = (seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainingSeconds}`;
};

const Otp = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isLoading,
    isGenerating,
    error,
    success,
    otpGenerated,
    maskedDestination,
  } = useSelector((state: RootState) => state.auth);

  const state = location.state as LocationState | null;
  const mobileNumber = state?.mobileNumber || '';
  const email = state?.email || 'johndoe@gmail.com';
  const transactionId = state?.transactionId || '';

  const [deliveryMethod, setDeliveryMethod] = useState<OtpDeliveryMethod | null>(
    state?.deliveryMethod || null
  );

  const [formValues, setFormValues] =
    useState<OtpFormValues>(initialFormValues);
  const [touched, setTouched] = useState<Record<keyof OtpFormValues, boolean>>({
    otp: false,
  });
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showGenerateSuccess, setShowGenerateSuccess] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(RESEND_DELAY_SECONDS);
  const [otpExpiryCountdown, setOtpExpiryCountdown] =
    useState(OTP_EXPIRY_SECONDS);
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const [verifyAttempts, setVerifyAttempts] = useState(0);
  const isSwitchingMethodRef = useRef(false);

  const validateField = (
    name: keyof OtpFormValues,
    value: string
  ): string | undefined => {
    switch (name) {
      case 'otp':
        if (!isRequired(value)) return AUTH_LABELS.otp.otpRequired;
        if (!isOnlyDigits(value)) return AUTH_LABELS.otp.otpOnlyDigits;
        if (!isExactLength(value, 6)) return AUTH_LABELS.otp.otpLengthInvalid;
        return undefined;
      default:
        return undefined;
    }
  };

  const errors = useMemo<FormErrors>(() => {
    return {
      otp: touched.otp ? validateField('otp', formValues.otp) : undefined,
    };
  }, [formValues, touched]);

  const isFormValid = useMemo(() => {
    const fieldNames: (keyof OtpFormValues)[] = Object.keys(
      formValues
    ) as (keyof OtpFormValues)[];
    return fieldNames.every((name) => !validateField(name, formValues[name]));
  }, [formValues]);

  const isLockedOut = useMemo(
    () => verifyAttempts >= MAX_OTP_ATTEMPTS && !!error,
    [verifyAttempts, error]
  );

  useEffect(() => {
    if (!transactionId) {
      navigate(AUTH_ROUTES.LOGIN, { replace: true });
      return;
    }
  }, [transactionId, navigate]);

  useEffect(() => {
    if (otpGenerated) {
      setShowGenerateSuccess(true);
      const timer = setTimeout(() => {
        setShowGenerateSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [otpGenerated]);

  useEffect(() => {
    const lowerError = error?.toLowerCase() || '';
    if (lowerError.includes(AUTH_LABELS.otp.authenticationRequiredError)) {
      dispatch(resetAuthState());
      navigate(AUTH_ROUTES.LOGIN, { replace: true });
    }
  }, [error, navigate, dispatch]);

  useEffect(() => {
    if (!error) {
      isSwitchingMethodRef.current = false;
      return;
    }

    if (
      isSwitchingMethodRef.current &&
      error.toLowerCase().includes(AUTH_LABELS.otp.rateLimitError)
    ) {
      dispatch(clearAuthError());
    }

    isSwitchingMethodRef.current = false;
  }, [error, dispatch]);

  useEffect(() => {
    if (resendCountdown <= 0) return;

    const timer = setInterval(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCountdown]);

  useEffect(() => {
    if (otpExpiryCountdown <= 0) {
      setIsOtpExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setOtpExpiryCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [otpExpiryCountdown]);

  useEffect(() => {
    // console.log('[Otp] Auth state', { success, error, isLoading, otpGenerated });
    if (success) {
      // console.log('[Otp] OTP verified; showing success toast and scheduling dashboard navigation');
      setShowSuccessToast(true);
      const timer = setTimeout(() => {
        // console.log('[Otp] Navigating to dashboard');
        navigate(AUTH_ROUTES.DASHBOARD, { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, error, isLoading, otpGenerated, navigate]);

  const submitOtp = (otpValue: string) => {
    setTouched({ otp: true });

    if (!otpValue || !transactionId || !deliveryMethod || isLockedOut || isOtpExpired || isLoading) {
      return;
    }

    setVerifyAttempts((prev) => prev + 1);

    dispatch(
      verifyOtpRequest({
        transactionId,
        code: otpValue,
        deliveryMethod,
        deviceInfo: getDeviceInfo(),
      })
    );
  };

  const handleOtpChange = (otp: string) => {
    if (error) {
      dispatch(clearAuthError());
    }
    const digitsOnly = otp.replace(/\D/g, '').slice(0, 6);
    setFormValues((prev: OtpFormValues) => ({ ...prev, otp: digitsOnly }));
    if (digitsOnly.length === 6) {
      submitOtp(digitsOnly);
    }
  };

  const handleOtpBlur = () => {
    setTouched((prev: Record<keyof OtpFormValues, boolean>) => ({
      ...prev,
      otp: true,
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isFormValid) {
      setTouched({ otp: true });
      return;
    }
    submitOtp(formValues.otp);
  };

  const handleResend = () => {
    if (resendCountdown > 0 || !transactionId || !deliveryMethod) return;
    setResendCountdown(RESEND_DELAY_SECONDS);
    setOtpExpiryCountdown(OTP_EXPIRY_SECONDS);
    setIsOtpExpired(false);
    setVerifyAttempts(0);
    setFormValues({ otp: '' });
    setTouched({ otp: false });
    dispatch(generateOtpRequest({ transactionId, deliveryMethod }));
  };

  const handleDeliveryMethodChange = (method: OtpDeliveryMethod) => {
    if (isLockedOut || isGenerating || method === deliveryMethod) {
      return;
    }

    dispatch(clearAuthError());

    isSwitchingMethodRef.current = true;

    setDeliveryMethod(method);
    setFormValues({ otp: '' });
    setTouched({ otp: false });
    setVerifyAttempts(0);
    setResendCountdown(RESEND_DELAY_SECONDS);
    setOtpExpiryCountdown(OTP_EXPIRY_SECONDS);
    setIsOtpExpired(false);

    dispatch(generateOtpRequest({ transactionId, deliveryMethod: method }));
  };

  const contactValue = useMemo(() => {
    if (maskedDestination) return maskedDestination;
    if (deliveryMethod === 'EMAIL') {
      return email ? maskEmail(email) : AUTH_LABELS.otp.contactNotAvailable;
    }
    return mobileNumber
      ? maskMobileNumber(mobileNumber)
      : AUTH_LABELS.otp.contactNotAvailable;
  }, [maskedDestination, deliveryMethod, email, mobileNumber]);

  return (
    <div className="nova-register-page">
      <div className="nova-register-container">
        {/* Left Hero Section */}
        <div className="nova-hero-section">
          <div className="nova-hero-content">
            <div className="nova-logo">
              <div className="nova-logo-mark">{AUTH_LABELS.app.logoMark}</div>
              <span className="nova-logo-text">{AUTH_LABELS.app.name}</span>
            </div>

            <h1 className="nova-hero-title">
              {AUTH_LABELS.hero.titleLine1}
              <br />
              {AUTH_LABELS.hero.titleLine2}
            </h1>

            <div className="nova-feature-cards">
              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faShieldHalved} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureSecureTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureSecureDescription}
                  </p>
                </div>
              </div>

              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureManageTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureManageDescription}
                  </p>
                </div>
              </div>

              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureBankingTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureBankingDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <img src={skyline} alt={AUTH_LABELS.hero.skylineAlt} className="nova-skyline" />
        </div>

        {/* Right Form Section */}
        <div className="nova-form-section">
          <div className="nova-form-card">
            <div className="nova-form-header">
              <h2 className="nova-form-title">{AUTH_LABELS.otp.title}</h2>
              <p className="nova-form-subtitle">{AUTH_LABELS.otp.subtitle}</p>

              {deliveryMethod && (
                <ContactInfo
                  type={deliveryMethod === 'EMAIL' ? 'email' : 'mobile'}
                  label={
                    deliveryMethod === 'EMAIL'
                      ? AUTH_LABELS.otp.emailLabel
                      : AUTH_LABELS.otp.mobileLabel
                  }
                  value={contactValue}
                />
              )}
            </div>

            {showGenerateSuccess && (
              <Alert
                variant="success"
                className="nova-alert"
                dismissible
                onClose={() => setShowGenerateSuccess(false)}
              >
                {AUTH_LABELS.otp.otpSentSuccess}
              </Alert>
            )}

            {error &&
              !isLockedOut &&
              !error.toLowerCase().includes(AUTH_LABELS.otp.authenticationRequiredError) && (
                <Alert
                  variant="danger"
                  className="nova-alert"
                  dismissible
                  onClose={() => dispatch(clearAuthError())}
                >
                  {error}
                </Alert>
              )}

            {isLockedOut && (
              <Alert variant="warning" className="nova-alert">
                {AUTH_LABELS.otp.lockedOutMessage}
              </Alert>
            )}

            {isGenerating && (
              <div className="nova-generate-loading">
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span>{AUTH_LABELS.otp.sendingOtp}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {!otpGenerated && (
                <DeliveryMethodSelector
                  value={deliveryMethod}
                  onChange={handleDeliveryMethodChange}
                  disabled={isLockedOut || isOtpExpired || isGenerating}
                  title={AUTH_LABELS.otp.deliveryMethodTitle}
                  emailLabel={AUTH_LABELS.otp.emailLabel}
                  mobileLabel={AUTH_LABELS.otp.mobileLabel}
                />
              )}

              {otpGenerated && deliveryMethod && (
                <>
                  <OtpInput
                    id="otp"
                    value={formValues.otp}
                    onChange={handleOtpChange}
                    onBlur={handleOtpBlur}
                    error={errors.otp}
                    label={
                      deliveryMethod === 'EMAIL'
                        ? AUTH_LABELS.otp.otpLabelEmail
                        : AUTH_LABELS.otp.otpLabelMobile
                    }
                    disabled={isLockedOut || isOtpExpired}
                  />

                  <div className="nova-countdown text-center mb-3">
                    {isOtpExpired ? (
                      <span className="text-danger">{AUTH_LABELS.otp.expired}</span>
                    ) : (
                      <>
                        {AUTH_LABELS.otp.otpExpiresIn}{' '}
                        {formatCountdown(otpExpiryCountdown)} min
                      </>
                    )}
                  </div>

                  <Button
                    type="submit"
                    isLoading={isLoading}
                    disabled={!isFormValid || isOtpExpired || isLockedOut}
                    className="w-100"
                  >
                    {isLockedOut
                      ? AUTH_LABELS.otp.lockedOutButton
                      : AUTH_LABELS.otp.verifyButton}
                  </Button>

                  <div className="nova-resend-section">
                    <span className="nova-resend-text">
                      {AUTH_LABELS.otp.resendText}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link nova-resend-link"
                      onClick={handleResend}
                      disabled={resendCountdown > 0 || isGenerating}
                    >
                      {resendCountdown > 0 ? (
                        <>
                          {AUTH_LABELS.otp.resendButtonWithCountdown(resendCountdown)}
                        </>
                      ) : (
                        <>{AUTH_LABELS.otp.resendButton}</>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>

            <div className="nova-form-footer">
              <p>
                {AUTH_LABELS.otp.backToLogin}{' '}
                <Link to={AUTH_ROUTES.LOGIN} className="nova-login-link">
                  {AUTH_LABELS.otp.backToLoginLink}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          delay={2000}
          autohide
          bg="success"
        >
          <Toast.Header closeButton>
            <strong className="me-auto">{AUTH_LABELS.app.name}</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{AUTH_LABELS.otp.loginSuccessToast}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Otp;
